import React, { useLayoutEffect, useState } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import moment from 'moment';
import { toastr } from 'react-redux-toastr';
import Immutable from 'immutable';
import Content from '../../../layout/Content';
import DriverTripHistoryForm from './DriverTripHistoryForm';
import Table from '../../../common/Table';
import { DRIVER_TRIP_HISTORY_COLUMNS } from '../../../../config/columns';
import { DEFAULT_PAGE_SIZE } from '../../../../config/constants';
import {
  clearDriverTripsByDateRange,
  getDriverTripsByDateRange,
  getCabinCrewTripsByDateRange,
  clearCabinCrewTrips,
} from '../../../../actions';
import { breadcrumbsPropTypes } from '../../../common/resource/proptypes/CommonPropTypes';
import { tzNormalizeDate } from '../../../../utils/date';
import { INT_DATE_FORMAT } from '../../../../config/locale';
import { DEFAULT_QUERY } from '../../../../config/queries';

const DriversTripHistory = ({
  dispatchGetDriverTripsByDateRange,
  dispatchClearDriverTripsByDateRange,
  dispatchGetCabinCrewTripsByDateRange,
  dispatchClearCabinCrewTripsByDateRange,
  breadcrumbs,
  driverTrips,
  cabinCrewTrips,
  loadingDriver,
  loadingCabinCrew,
}) => {
  const [personType, setPersonType] = useState(null);
  const [personId, setPersonId] = useState(null);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);

  useLayoutEffect(
    () => () => {
      dispatchClearDriverTripsByDateRange();
      dispatchClearCabinCrewTripsByDateRange();
    },
    [],
  );

  const validateDates = (validateStartDate, validateEndDate) => {
    if (validateStartDate && validateEndDate) {
      if (moment(validateStartDate).isAfter(validateEndDate)) {
        toastr.error(
          'Error',
          'La fecha de inicio no puede ser mayor a la fecha de fin',
        );
        return false;
      }
    }

    return true;
  };

  const onSubmit = (formValues) => {
    const driverSelected = formValues.driver && formValues.driver.value;
    const cabinCrewSelected = formValues.cabinCrew && formValues.cabinCrew.value;

    if (!driverSelected && !cabinCrewSelected) {
      toastr.error('Error', 'Seleccione un conductor o un tripulante');
      return;
    }

    const type = cabinCrewSelected ? 'cabinCrew' : 'driver';
    const id = cabinCrewSelected ? formValues.cabinCrew.value : formValues.driver.value;

    setPersonType(type);
    setPersonId(id);

    if (!validateDates(formValues.startDate, formValues.endDate)) {
      return;
    }

    const formattedStartDate = formValues.startDate
      ? tzNormalizeDate({ date: formValues.startDate, format: INT_DATE_FORMAT })
      : null;

    const formattedEndDate = formValues.endDate
      ? tzNormalizeDate({ date: formValues.endDate, format: INT_DATE_FORMAT })
      : null;

    setStartDate(formattedStartDate);

    setEndDate(formattedEndDate);

    const searchFormValues = {
      startDate: formattedStartDate,
      endDate: formattedEndDate,
      ...DEFAULT_QUERY,
    };

    if (type === 'cabinCrew') {
      dispatchGetCabinCrewTripsByDateRange(id, searchFormValues);
    } else {
      dispatchGetDriverTripsByDateRange(id, searchFormValues);
    }
  };

  const onFetchData = (searchFormValues) => {
    if (!personId) return;

    validateDates(searchFormValues.startDate, searchFormValues.endDate);

    const params = { ...searchFormValues, startDate, endDate };

    if (personType === 'cabinCrew') {
      dispatchGetCabinCrewTripsByDateRange(personId, params);
    } else {
      dispatchGetDriverTripsByDateRange(personId, params);
    }
  };

  const trips = personType === 'cabinCrew' ? cabinCrewTrips : driverTrips;
  const loading = personType === 'cabinCrew' ? loadingCabinCrew : loadingDriver;

  const data = trips.get('content') || [];
  const columns = DRIVER_TRIP_HISTORY_COLUMNS;
  const pages = trips.get('totalPages') || null;
  const defaultPageSize = trips.get('size') || DEFAULT_PAGE_SIZE;

  return (
    <Content
      title="Historial de viajes"
      subtitle="Historial de viajes por Conductor o Tripulante"
      breadcrumbs={breadcrumbs}
      content={
        <>
          <DriverTripHistoryForm onSubmit={onSubmit} />
          <Table
            columns={columns}
            data={data}
            loading={loading}
            pages={pages}
            defaultPageSize={defaultPageSize}
            fetchData={onFetchData}
          />
        </>
      }
    />
  );
};

DriversTripHistory.propTypes = {
  breadcrumbs: breadcrumbsPropTypes.isRequired,
  dispatchGetDriverTripsByDateRange: PropTypes.func.isRequired,
  dispatchClearDriverTripsByDateRange: PropTypes.func.isRequired,
  dispatchGetCabinCrewTripsByDateRange: PropTypes.func.isRequired,
  dispatchClearCabinCrewTripsByDateRange: PropTypes.func.isRequired,
  driverTrips: PropTypes.instanceOf(Immutable.Map).isRequired,
  cabinCrewTrips: PropTypes.instanceOf(Immutable.Map).isRequired,
  loadingDriver: PropTypes.bool,
  loadingCabinCrew: PropTypes.bool,
};

DriversTripHistory.defaultProps = {
  loadingDriver: false,
  loadingCabinCrew: false,
};

const mapStateToProps = ({ TrafficUnit }) => ({
  breadcrumbs: [
    ...TrafficUnit.UnitHome.get('breadcrumbs'),
    {
      text: 'Viajes Realizados',
      href: '',
    },
  ],
  driverTrips: TrafficUnit.Driver.getIn(['current', 'trips']),
  loadingDriver: !TrafficUnit.Driver.getIn(['current', 'activity']).isEmpty(),
  cabinCrewTrips: TrafficUnit.CabinCrew.getIn(['current', 'trips']),
  loadingCabinCrew: !TrafficUnit.CabinCrew.getIn(['current', 'activity']).isEmpty(),
});

const mapDispatchToProps = {
  dispatchGetDriverTripsByDateRange: getDriverTripsByDateRange,
  dispatchClearDriverTripsByDateRange: clearDriverTripsByDateRange,
  dispatchGetCabinCrewTripsByDateRange: getCabinCrewTripsByDateRange,
  dispatchClearCabinCrewTripsByDateRange: clearCabinCrewTrips,
};

export default connect(mapStateToProps, mapDispatchToProps)(DriversTripHistory);